import { useState, useEffect } from 'react'
import { salespersonAPI, salespersonMyAPI } from '../../services/api'

const PAGE_SIZE = 10

function SalespersonDetailPage({ salespersonId, onBack, readOnly = false }) {
  const [salesperson, setSalesperson] = useState(null)
  const [referrals, setReferrals] = useState([])
  const [stats, setStats] = useState({})
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [showCommissionModal, setShowCommissionModal] = useState(false)
  const [newRate, setNewRate] = useState('')

  useEffect(() => {
    loadData()
  }, [salespersonId])

  const loadData = async () => {
    try {
      setLoading(true)
      const api = readOnly ? salespersonMyAPI : salespersonAPI
      const [spRes, refRes, statsRes] = await Promise.all([
        readOnly ? api.getMyInfo() : api.getById(salespersonId),
        readOnly ? api.getMyReferrals() : api.getReferrals(salespersonId),
        readOnly ? api.getMyDetailStats() : api.getDetailStats(salespersonId),
      ])
      setSalesperson(spRes.data)
      setReferrals(refRes.data || [])
      setStats(statsRes.data || {})
    } catch (err) {
      console.error('상세 데이터 로드 실패:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCommissionUpdate = async () => {
    const rate = parseFloat(newRate)
    if (isNaN(rate) || rate < 0 || rate > 100) return
    try {
      await salespersonAPI.updateCommission(salespersonId, rate)
      setShowCommissionModal(false)
      setNewRate('')
      await loadData()
    } catch (err) {
      console.error('수수료율 변경 실패:', err)
    }
  }

  const handleSettlementToggle = async (referralId, currentStatus) => {
    const newStatus = currentStatus === 'COMPLETED' ? 'PENDING' : 'COMPLETED'
    try {
      await salespersonAPI.updateSettlement(referralId, newStatus)
      await loadData()
    } catch (err) {
      console.error('정산 상태 변경 실패:', err)
    }
  }

  // CSV 리포트 추출
  const handleExportReport = () => {
    if (referrals.length === 0) return
    const header = '유저ID,유저명,가입일,구독 플랜,총 결제액,누적 수수료,정산 상태\n'
    const rows = referrals.map(ref =>
      `${ref.userId},${ref.userName},${ref.createdAt ? formatDate(ref.createdAt) : '-'},${ref.plan},${ref.paymentAmount},${ref.commission || 0},${ref.settlementStatus === 'COMPLETED' ? '완료' : '대기중'}`
    ).join('\n')
    const bom = '\uFEFF'
    const blob = new Blob([bom + header + rows], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${salesperson?.name}_리포트.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    const d = new Date(dateStr)
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`
  }

  // 페이지네이션
  const totalPages = Math.max(1, Math.ceil(referrals.length / PAGE_SIZE))
  const paginatedReferrals = referrals.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  )

  // 월별 차트 데이터 생성 (최근 6개월)
  const getMonthlyChartData = () => {
    const months = []
    const now = new Date()
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const monthLabel = `${d.getMonth() + 1}월`
      const monthStart = new Date(d.getFullYear(), d.getMonth(), 1)
      const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59)

      const monthRefs = referrals.filter(ref => {
        if (!ref.createdAt) return false
        const refDate = new Date(ref.createdAt)
        return refDate >= monthStart && refDate <= monthEnd
      })

      const premium = monthRefs.filter(r => r.plan === 'PREMIUM' || r.plan === 'ENTERPRISE').length
      const basic = monthRefs.filter(r => r.plan === 'BASIC').length

      months.push({ label: monthLabel, premium, basic, total: premium + basic })
    }
    return months
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-slate-500 dark:text-slate-400 text-lg">로딩 중...</div>
      </div>
    )
  }

  if (!salesperson) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-slate-500">영업사원을 찾을 수 없습니다.</div>
      </div>
    )
  }

  const chartData = getMonthlyChartData()
  const maxChart = Math.max(...chartData.map(d => d.total), 1)

  return (
    <div className="space-y-8">
      {/* 헤더 */}
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-500 transition-colors"
            >
              <span className="material-symbols-outlined">arrow_back</span>
            </button>
          )}
          <h2 className="text-lg font-bold">{readOnly ? '내 실적' : '에이전트 상세 관리'}</h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">search</span>
            <input
              className="pl-9 pr-4 py-2 bg-slate-100 dark:bg-slate-800 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary w-64"
              placeholder="검색..."
              type="text"
            />
          </div>
          <button className="p-2 bg-slate-100 dark:bg-slate-800 rounded-xl text-slate-600 dark:text-slate-300 relative">
            <span className="material-symbols-outlined">notifications</span>
            <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-white dark:border-background-dark"></span>
          </button>
        </div>
      </header>

      {/* 에이전트 프로필 섹션 */}
      <section className="bg-white dark:bg-slate-900/40 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center gap-5">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden ring-4 ring-primary/10 flex items-center justify-center text-3xl font-bold text-primary">
              {salesperson.name?.charAt(0)}
            </div>
            {salesperson.status === 'ACTIVE' && (
              <div className="absolute bottom-0 right-0 bg-green-500 w-5 h-5 rounded-full border-4 border-white dark:border-slate-900"></div>
            )}
          </div>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h3 className="text-2xl font-bold">{salesperson.name}</h3>
              {salesperson.referralCount >= 100 && (
                <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded">Top Agent</span>
              )}
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-slate-500">
              <div className="flex items-center gap-1.5">
                <span className="material-symbols-outlined text-sm">id_card</span>
                <span className="font-mono">{salesperson.referralCode}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="material-symbols-outlined text-sm">percent</span>
                <span className="text-slate-900 dark:text-slate-100 font-semibold">현재 수수료율: {salesperson.commissionRate || 15}%</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="material-symbols-outlined text-sm">schedule</span>
                <span>최종 접속: {salesperson.lastAccessTime ? formatDate(salesperson.lastAccessTime) : formatDate(salesperson.createdAt)}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {!readOnly && (
            <>
              <button
                onClick={() => { setNewRate(String(salesperson.commissionRate || 15)); setShowCommissionModal(true) }}
                className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-sm font-semibold rounded-xl transition-all flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-sm">edit</span> 수수료율 변경
              </button>
              <button
                onClick={() => alert(`${salesperson.name}에게 메시지를 보내는 기능은 준비 중입니다.`)}
                className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-sm font-semibold rounded-xl transition-all flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-sm">mail</span> 메시지
              </button>
            </>
          )}
          <button
            onClick={handleExportReport}
            className="px-4 py-2 bg-primary text-white hover:bg-primary/90 text-sm font-semibold rounded-xl transition-all flex items-center gap-2 shadow-lg shadow-primary/20"
          >
            <span className="material-symbols-outlined text-sm">download</span> 리포트 추출
          </button>
        </div>
      </section>

      {/* 통계 카드 4개 */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 이번 달 신규 유저 */}
        <div className="bg-white dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 p-5 rounded-2xl">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">이번 달 신규 유저</span>
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center">
              <span className="material-symbols-outlined text-sm">person_add</span>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <span className="text-2xl font-bold">{stats.monthlyReferrals || 0}명</span>
            <span className="text-xs font-bold text-emerald-500 pb-1">+12.5%</span>
          </div>
          <div className="mt-4 h-1 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(100, (stats.monthlyReferrals || 0) / Math.max(stats.totalReferrals || 1, 1) * 100)}%` }}></div>
          </div>
        </div>

        {/* 전체 누적 유저 */}
        <div className="bg-white dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 p-5 rounded-2xl">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">전체 누적 유저</span>
            <div className="w-8 h-8 rounded-lg bg-purple-500/10 text-purple-500 flex items-center justify-center">
              <span className="material-symbols-outlined text-sm">group</span>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <span className="text-2xl font-bold">{(stats.totalReferrals || 0).toLocaleString()}명</span>
            <span className="text-xs font-bold text-emerald-500 pb-1">+5.2%</span>
          </div>
          <div className="mt-4 h-1 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-purple-500 rounded-full" style={{ width: `${Math.min(100, Math.max(10, (stats.totalReferrals || 0) / Math.max(stats.totalReferrals || 1, 100) * 100))}%` }}></div>
          </div>
        </div>

        {/* 총 누적 수수료 */}
        <div className="bg-white dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 p-5 rounded-2xl">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">총 누적 수수료</span>
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
              <span className="material-symbols-outlined text-sm">account_balance_wallet</span>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <span className="text-2xl font-bold">₩{(stats.totalCommission || 0).toLocaleString()}</span>
            <span className="text-xs font-bold text-emerald-500 pb-1">+8.1%</span>
          </div>
          <div className="mt-4 h-1 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, Math.max(10, (stats.totalCommission || 0) / Math.max((stats.totalCommission || 0) + (stats.pendingCommission || 0), 1) * 100))}%` }}></div>
          </div>
        </div>

        {/* 정산 대기 금액 */}
        <div className="bg-white dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 p-5 rounded-2xl">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">정산 대기 금액</span>
            <div className="w-8 h-8 rounded-lg bg-orange-500/10 text-orange-500 flex items-center justify-center">
              <span className="material-symbols-outlined text-sm">hourglass_empty</span>
            </div>
          </div>
          <div className="flex items-end gap-2">
            <span className="text-2xl font-bold">₩{(stats.pendingCommission || 0).toLocaleString()}</span>
            <span className="text-xs font-bold text-slate-400 pb-1">0%</span>
          </div>
          <div className="mt-4 h-1 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-orange-500 rounded-full" style={{ width: `${stats.totalCommission ? Math.round((stats.pendingCommission || 0) / stats.totalCommission * 100) : 30}%` }}></div>
          </div>
        </div>
      </section>

      {/* 영업 성장 추이 차트 */}
      <div className="bg-white dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h4 className="text-lg font-bold">영업 성장 추이</h4>
            <p className="text-sm text-slate-500">최근 6개월간 신규 가입자 수</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5 text-xs text-slate-500">
              <span className="w-3 h-3 rounded-full bg-primary"></span> 프리미엄
            </span>
            <span className="flex items-center gap-1.5 text-xs text-slate-500">
              <span className="w-3 h-3 rounded-full bg-primary/30"></span> 베이직
            </span>
          </div>
        </div>
        <div className="h-64 w-full flex items-end justify-between gap-4 px-2">
          {chartData.map((month, idx) => (
            <div key={idx} className="flex-1 flex flex-col items-center gap-2">
              <div className="w-full flex flex-col items-center justify-end gap-1 h-48">
                <div
                  className="w-8 sm:w-12 bg-primary/30 rounded-t-lg transition-all"
                  style={{ height: `${maxChart > 0 ? (month.basic / maxChart) * 100 : 0}%`, minHeight: month.basic > 0 ? '8px' : '0' }}
                ></div>
                <div
                  className="w-8 sm:w-12 bg-primary rounded-t-lg transition-all"
                  style={{ height: `${maxChart > 0 ? (month.premium / maxChart) * 100 : 0}%`, minHeight: month.premium > 0 ? '8px' : '0' }}
                ></div>
              </div>
              <span className={`text-xs font-medium ${idx === chartData.length - 1 ? 'text-primary font-bold' : 'text-slate-500'}`}>
                {month.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* 관리 유저 리스트 */}
      <div className="bg-white dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
        <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <h4 className="text-lg font-bold">관리 유저 리스트</h4>
          <button className="text-sm font-medium text-primary hover:underline">전체 보기</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50/50 dark:bg-slate-800/30 text-xs text-slate-500 font-semibold uppercase tracking-wider">
              <tr>
                <th className="px-6 py-4">유저 ID / 이름</th>
                <th className="px-6 py-4">가입일</th>
                <th className="px-6 py-4">구독 플랜</th>
                <th className="px-6 py-4">총 결제액</th>
                <th className="px-6 py-4">누적 수수료</th>
                <th className="px-6 py-4">정산 상태</th>
                {!readOnly && <th className="px-6 py-4">작업</th>}
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-800">
              {paginatedReferrals.length === 0 ? (
                <tr>
                  <td colSpan={readOnly ? 6 : 7} className="px-6 py-8 text-center text-sm text-slate-500">
                    등록된 유저가 없습니다
                  </td>
                </tr>
              ) : (
                paginatedReferrals.map(ref => (
                  <tr key={ref.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/20 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-bold">
                          {ref.userName?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-bold">{ref.userId}</p>
                          <p className="text-xs text-slate-500">{ref.userName}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-500">{formatDate(ref.createdAt)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-[10px] font-bold rounded-md uppercase ${
                        ref.plan === 'PREMIUM' || ref.plan === 'ENTERPRISE'
                          ? 'bg-primary/10 text-primary'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                      }`}>
                        {ref.plan}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-medium">₩{ref.paymentAmount?.toLocaleString()}</td>
                    <td className="px-6 py-4 font-medium text-emerald-500">₩{(ref.commission || 0).toLocaleString()}</td>
                    <td className="px-6 py-4">
                      <span className={`flex items-center gap-1.5 text-xs font-semibold ${
                        ref.settlementStatus === 'COMPLETED'
                          ? 'text-emerald-500'
                          : 'text-orange-500'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          ref.settlementStatus === 'COMPLETED' ? 'bg-emerald-500' : 'bg-orange-500'
                        }`}></span>
                        {ref.settlementStatus === 'COMPLETED' ? '완료' : '대기중'}
                      </span>
                    </td>
                    {!readOnly && (
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleSettlementToggle(ref.id, ref.settlementStatus)}
                          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-all"
                        >
                          <span className="material-symbols-outlined text-sm">settings</span>
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* 페이지네이션 */}
        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <span className="text-xs text-slate-500">
            {referrals.length}명의 사용자 중 {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, referrals.length)} 표시 중
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded text-slate-400 disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-lg">chevron_left</span>
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (currentPage <= 3) {
                  pageNum = i + 1
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = currentPage - 2 + i
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-6 h-6 rounded text-xs font-bold ${
                      currentPage === pageNum
                        ? 'bg-primary text-white'
                        : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded text-slate-400 disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-lg">chevron_right</span>
            </button>
          </div>
        </div>
      </div>

      {/* 수수료율 변경 모달 */}
      {!readOnly && showCommissionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 w-full max-w-sm border border-slate-200 dark:border-slate-800" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">수수료율 변경</h3>
            <p className="text-sm text-slate-500 mb-4">
              <span className="font-semibold text-slate-900 dark:text-white">{salesperson.name}</span>의 수수료율을 변경합니다.
            </p>
            <div className="relative">
              <input
                type="number"
                min="0"
                max="100"
                step="0.5"
                className="w-full px-3 py-2 pr-8 text-sm bg-slate-100 dark:bg-slate-800 border-none rounded-lg focus:ring-2 focus:ring-primary"
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
                placeholder="수수료율 입력"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCommissionModal(false)}
                className="flex-1 py-2 text-sm font-medium text-slate-500 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                취소
              </button>
              <button
                onClick={handleCommissionUpdate}
                disabled={!newRate || parseFloat(newRate) < 0 || parseFloat(newRate) > 100}
                className="flex-1 py-2 text-sm font-bold text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                변경하기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SalespersonDetailPage
